/*
 * Platform admin: plans and billing set-up, the platform's own integrations,
 * free-trial applications, website settings and website traffic. Also the
 * tenant-side integration apps (tenant/integrations/...), which no screen
 * calls yet but the API client offers.
 *
 * Shapes follow the Admin controllers (PlanController, PaymentMethodController,
 * PaymentScheduleController, PlatformIntegrationController,
 * TrialApplicationController, WebsiteSettingController, WebsiteTrafficController)
 * and Tenant/Integration/IntegrationAppController.
 */
import { platformPlans, platformModules, tenants } from "@/mock/admin";
import { connectedApps } from "@/mock/support";
import { ok, created, fail, notFound, invalid, paginate, pageMeta, matches, hoursAgo, daysAgo, now } from "./util";

const ADMIN = "Platform Admin";
const origin = () => (typeof window !== "undefined" ? window.location.origin : "https://admin.deecrm360.com");
const token = (prefix, n = 40) => prefix + Array.from({ length: n }, () => "abcdefghijkmnpqrstuvwxyz23456789"[Math.floor(Math.random() * 32)]).join("");

/* ------------------------------------------------------------------ */
/* Payment schedules and methods                                        */
/* ------------------------------------------------------------------ */

const schedules = [
  { id: "sch-monthly", name: "Monthly", months: 1, discount_percent: 0, due_days: 7, description: "Invoiced at the start of each month.", sort_order: 10, is_active: true },
  { id: "sch-quarterly", name: "Quarterly", months: 3, discount_percent: 5, due_days: 14, description: "Three months at a time, 5% off.", sort_order: 30, is_active: true },
  { id: "sch-annual", name: "Annual", months: 12, discount_percent: 15, due_days: 30, description: "A year in advance, 15% off.", sort_order: 120, is_active: true },
];

const METHOD_TYPES = {
  mpesa_paybill: { label: "M-Pesa Paybill", fields: { business_number: ["Paybill number", true], account_reference: ["Account number to enter", true] } },
  mpesa_till: { label: "M-Pesa Till (Buy Goods)", fields: { till_number: ["Till number", true] } },
  bank_transfer: {
    label: "Bank transfer",
    fields: {
      bank_name: ["Bank", true], branch: ["Branch", false], account_name: ["Account name", true], account_number: ["Account number", true],
      bank_code: ["Bank code", false], branch_code: ["Branch code", false], swift_code: ["SWIFT code", false],
    },
  },
  cheque: { label: "Cheque", fields: { payable_to: ["Payable to", true], deliver_to: ["Deliver or post to", false] } },
  other: { label: "Other", fields: {} },
};
const AUTOMATIC = ["mpesa_paybill", "mpesa_till"];
const CONFIRMATIONS = { automatic: "Confirmed automatically by M-Pesa", manual: "Recorded by our team, then reconciled when it clears" };

// The steps an invoice prints (PaymentMethod::howToPay).
const howToPay = (m) => {
  const d = m.details || {};
  const v = (k) => d[k] || "—";
  const steps = {
    mpesa_paybill: ["Go to M-Pesa, then Lipa na M-Pesa, then Pay Bill.", `Business number: ${v("business_number")}`, `Account number: ${v("account_reference")}`, "Enter the amount on your invoice and confirm."],
    mpesa_till: ["Go to M-Pesa, then Lipa na M-Pesa, then Buy Goods and Services.", `Till number: ${v("till_number")}`, "Enter the amount on your invoice and confirm."],
    bank_transfer: [
      `Bank: ${v("bank_name")}${d.branch ? `, ${d.branch} branch` : ""}`, `Account name: ${v("account_name")}`, `Account number: ${v("account_number")}`,
      d.bank_code || d.branch_code ? `Bank and branch code: ${[d.bank_code, d.branch_code].filter(Boolean).join(" / ")}` : null,
      d.swift_code ? `SWIFT: ${d.swift_code}` : null, "Use your invoice number as the payment reference.",
    ],
    cheque: [`Make the cheque payable to ${v("payable_to")}.`, d.deliver_to ? `Deliver or post it to ${d.deliver_to}.` : null, "Write your invoice number on the back."],
  }[m.type] || [];
  return [...steps, m.instructions || null].filter(Boolean);
};

const methods = [
  {
    id: "pm-paybill", name: "M-Pesa Paybill", type: "mpesa_paybill", details: { business_number: "4087231", account_reference: "Your company code, e.g. CRM001" },
    instructions: null, sort_order: 10, is_active: true, confirmation: "automatic", clearing_days: null,
  },
  {
    id: "pm-bank", name: "Bank transfer (KCB)", type: "bank_transfer",
    details: { bank_name: "KCB Bank Kenya", branch: "Upper Hill", account_name: "DeeCRM360 Ltd", account_number: "1298374650", bank_code: "01", branch_code: "091", swift_code: "KCBLKENX" },
    instructions: "Email the remittance advice to billing@deecrm360.com.", sort_order: 20, is_active: true, confirmation: "manual", clearing_days: 2,
  },
  {
    id: "pm-card", name: "Card", type: "other", details: {}, instructions: "Pay by Visa or Mastercard from the payment link on your invoice.",
    sort_order: 30, is_active: true, confirmation: "manual", clearing_days: 1,
  },
];

const presentMethod = (m) => ({
  ...m, details: { ...(m.details || {}) }, type_label: METHOD_TYPES[m.type]?.label ?? m.type, how_to_pay: howToPay(m),
  confirmation_label: CONFIRMATIONS[m.confirmation] ?? m.confirmation,
});

/* ------------------------------------------------------------------ */
/* Plans and the modules catalogue they are built from                  */
/* ------------------------------------------------------------------ */

// Module ids follow platformModules keys (mod-<key>), so they line up with the catalogue.
const PARTS = {
  prospects: ["Prospect register", "Qualification checklist", "Bulk import"],
  leads: ["Leads", "Opportunities & pipeline", "Win / loss reasons"],
  quotations: ["Quotations", "Price approvals", "Quotation templates"],
  demos: ["Demo scheduling", "Demo feedback"],
  onboarding: ["Onboarding plans", "UAT & sign-off", "Go-live checklist"],
  success: ["Health scores", "Success plans", "QBR reviews"],
  support: ["Tickets & SLAs", "Complaints", "Ticket API & webhooks"],
  renewals: ["Renewal pipeline", "Price uplift"],
  churn: ["Churn risk", "Exit process"],
  reports: ["Standard reports", "Dashboards", "Scheduled reports"],
  forecast: ["Sales forecast", "KPI engine"],
  portal: ["Customer portal"],
  whatsapp: ["WhatsApp inbox"],
};

const catalogue = [
  { id: "mod-core", name: "Core: users, roles & settings", is_core: true, submodules: ["Users & roles", "Company settings", "Audit trail"].map((name, i) => ({ id: `sub-core-${i + 1}`, name, description: null })) },
  ...platformModules.map((m) => ({
    id: `mod-${m.key}`, name: m.name, is_core: false,
    submodules: (PARTS[m.key] || [m.name]).map((name, i) => ({ id: `sub-${m.key}-${i + 1}`, name, description: null })),
  })),
];
const subsOf = (planKey) => catalogue.filter((m) => !m.is_core && platformModules.find((p) => `mod-${p.key}` === m.id)?.plans.includes(planKey)).flatMap((m) => m.submodules.map((s) => s.id));

// Plans are priced per user a month; the platform plan's monthly price over its seats.
const plans = [
  {
    id: "plan-trial", name: "Free trial", code: "free-trial", description: "Everything in DeeCRM360 for 30 days, to try it with your own team.",
    is_trial: true, trial_days: 30, includes_everything: true, price_per_user: 0, currency: "KES", min_users: null, max_users: 25,
    is_public: true, is_highlighted: false, is_active: true, sort_order: 0, submodule_ids: [], schedules: [],
  },
  ...platformPlans.map((p, i) => ({
    id: `plan-${p.key}`, name: p.name, code: p.key,
    description: { starter: "For small sales teams getting their pipeline in one place.", business: "For growing companies running sales, onboarding and support together.", enterprise: "For large teams that need forecasting, a customer portal and every channel." }[p.key],
    is_trial: false, trial_days: null, includes_everything: p.key === "enterprise", price_per_user: Math.round(p.price / p.users), currency: "KES",
    min_users: i === 0 ? 1 : platformPlans[i - 1].users + 1, max_users: p.key === "enterprise" ? null : p.users,
    is_public: true, is_highlighted: p.key === "business", is_active: true, sort_order: (i + 1) * 10,
    submodule_ids: p.key === "enterprise" ? [] : subsOf(p.key),
    schedules: (p.key === "starter" ? ["sch-monthly", "sch-quarterly"] : ["sch-monthly", "sch-quarterly", "sch-annual"]).map((id) => ({ id, discount_percent: p.key === "enterprise" && id === "sch-annual" ? 20 : null })),
  })),
];

const presentPlan = (p) => {
  const included = p.includes_everything ? catalogue.flatMap((m) => m.submodules.map((s) => ({ ...s, module_id: m.id })))
    : catalogue.flatMap((m) => m.submodules.filter((s) => m.is_core || p.submodule_ids.includes(s.id)).map((s) => ({ ...s, module_id: m.id })));
  return {
    ...p,
    submodule_ids: [...p.submodule_ids],
    included: { submodules: included.length, modules: new Set(included.map((s) => s.module_id)).size },
    schedules: p.schedules
      .map((link) => ({ link, s: schedules.find((x) => x.id === link.id) }))
      .filter(({ s }) => s)
      .sort((a, b) => a.s.months - b.s.months)
      .map(({ link, s }) => {
        const discount = Number(link.discount_percent ?? s.discount_percent);
        const full = p.price_per_user * s.months;
        const invoice = Math.round(full * (1 - discount / 100) * 100) / 100;
        return {
          id: s.id, name: s.name, months: s.months, own_discount: link.discount_percent !== null && link.discount_percent !== undefined ? Number(link.discount_percent) : null,
          discount_percent: discount, invoice, saving: Math.round((full - invoice) * 100) / 100,
          per_user_month: Math.round(p.price_per_user * (1 - discount / 100) * 100) / 100,
        };
      }),
  };
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "plan";

const planFrom = (b, plan = {}) => {
  const errors = {};
  if (!b.name) errors.name = ["The name field is required."];
  else if (plans.some((p) => p.name === b.name && p.id !== plan.id)) errors.name = ["There is already a plan with that name."];
  if (b.is_trial) {
    if (!b.trial_days) errors.trial_days = ["Say how many days the trial lasts."];
  } else {
    if (!(Number(b.price_per_user) > 0)) errors.price_per_user = ["Set the price per user per month."];
    if (!b.schedules?.length) errors.schedules = ["Choose at least one payment schedule this plan can be bought on."];
  }
  if (!b.includes_everything && !b.submodule_ids?.length) errors.submodule_ids = ["Choose the modules this plan includes, or let it include everything."];
  if (b.min_users && b.max_users && b.max_users < b.min_users) errors.max_users = ["The most users cannot be fewer than the fewest."];
  if (Object.keys(errors).length) return { errors };

  return {
    plan: {
      ...plan, name: b.name, description: b.description ?? null, is_trial: !!b.is_trial, trial_days: b.is_trial ? Number(b.trial_days) : null,
      includes_everything: !!b.includes_everything, price_per_user: b.is_trial ? 0 : Number(b.price_per_user), currency: "KES",
      min_users: b.min_users ?? null, max_users: b.max_users ?? null, is_public: !!b.is_public, is_highlighted: !!b.is_highlighted,
      is_active: !!b.is_active, sort_order: b.sort_order ?? plan.sort_order ?? 100,
      submodule_ids: b.includes_everything ? [] : [...b.submodule_ids],
      schedules: b.is_trial ? [] : b.schedules.map((s) => ({ id: s.id, discount_percent: s.discount_percent ?? null })),
    },
  };
};

const savePlan = (p) => {
  // One plan carries the "most chosen" mark on the website.
  if (p.is_highlighted) plans.forEach((x) => { if (x.id !== p.id) x.is_highlighted = false; });
};

/* ------------------------------------------------------------------ */
/* The platform's own integrations (config/platform-integrations.php)   */
/* ------------------------------------------------------------------ */

const points = [
  { key: "connection.test", name: "Check the connection", method: "GET", path: "/api/v1/platform/connection", always: true, rate_limit: 30, description: "Confirms the token works from this address. Returns the app name." },
  { key: "website.plans", name: "Read plans and prices", method: "GET", path: "/api/v1/platform/website/plans", rate_limit: 120, description: "Plans marked for the website, their prices on each payment schedule, the modules each includes, and the full modules catalogue for comparison." },
  { key: "website.content", name: "Read contact details", method: "GET", path: "/api/v1/platform/website/content", rate_limit: 120, description: "The contact details and social pages set under Website Settings, for the footer and contact links." },
  { key: "website.trial-application", name: "Send a free-trial application", method: "POST", path: "/api/v1/platform/website/trial-applications", rate_limit: 30, description: "A company applying for the free trial. Multipart form; up to three documents (PDF, JPG or PNG, 10 MB each). Returns the application reference." },
  {
    key: "website.visits", name: "Report page views", method: "POST", path: "/api/v1/platform/website/visits", rate_limit: 120,
    description: "Page views on the website, sent in batches by the website's server. Crawlers and link previews are dropped. Returns how many were recorded.",
    fields: { visits: "array of up to 200: path (required), referrer, ip, user_agent, utm_source, utm_medium, utm_campaign, visited_at", site_host: "the website's own host, so moving between its pages is not counted as a referral" },
  },
  {
    key: "website.find-company", name: "Find where an email signs in", method: "POST", path: "/api/v1/platform/website/find-company", rate_limit: 120,
    description: "The website's Sign in: the companies an email address can sign in to, each with its sign-in link (email filled in). 404 when there are none. Limited to 10 tries a minute per visitor.",
    fields: { email: "string, required", visitor_ip: "the visitor's IP address, for the per-visitor limit" },
  },
];

const WEB_IP = "102.68.77.14";
const callLog = (n) =>
  Array.from({ length: n }, (_, i) => {
    const point = ["website.visits", "website.plans", "website.content", "website.visits", "website.find-company", "website.trial-application"][i % 6];
    const refused = i === 7 || i === 19;
    return {
      id: 9000 - i, app_id: "pia-website", point, ip: refused ? "41.80.112.9" : WEB_IP, created_at: hoursAgo(i * 1.7 + 0.1),
      status_code: refused ? 403 : point === "website.find-company" && i % 4 === 0 ? 404 : point === "website.trial-application" ? 201 : 200,
      duration_ms: 40 + ((i * 37) % 180), outcome: refused ? "refused" : "ok", reason: refused ? "Address not allowed for this app" : null,
    };
  });

const platformApps = [
  {
    id: "pia-website", name: "DeeCRM360 website", description: "The public website at deecrm360.com: pricing, trial sign-up and sign-in finder.", status: "active",
    ip_addresses: [WEB_IP, "102.68.77.15"], points: points.map((p) => p.key), rate_limit_per_minute: null,
    refresh_url: "https://deecrm360.com/api/refresh", has_refresh_secret: true, last_refreshed_at: hoursAgo(5), last_refresh_result: "ok: website cache cleared",
    last_used_at: hoursAgo(0.1), last_used_ip: WEB_IP,
    tokens: [
      { id: "pit-1", name: "Website server", last_four: "q7m2", state: "active", expires_at: null, revoked_at: null, last_used_at: hoursAgo(0.1), last_used_ip: WEB_IP, created_at: daysAgo(140), created_by_name: ADMIN },
      { id: "pit-2", name: "Old staging server", last_four: "a91c", state: "revoked", expires_at: null, revoked_at: daysAgo(30), last_used_at: daysAgo(31), last_used_ip: "102.68.77.20", created_at: daysAgo(200), created_by_name: ADMIN },
    ],
    calls: callLog(40),
  },
];

const presentPlatformApp = (a) => {
  const week = a.calls.filter((c) => Date.parse(c.created_at) >= Date.now() - 7 * 864e5);
  return { ...a, tokens: a.tokens.map((t) => ({ ...t })), calls: { week: week.length, refused: week.filter((c) => c.outcome === "refused").length, recent: week.slice(0, 25) } };
};

const appFrom = (b, app = {}) => {
  const errors = {};
  if (!b.name) errors.name = ["The name field is required."];
  else if (platformApps.some((a) => a.name === b.name && a.id !== app.id)) errors.name = ["The name has already been taken."];
  if (!b.ip_addresses?.length) errors.ip_addresses = ["Add the IP address of the server that will call DeeCRM360."];
  if (!b.points?.length) errors.points = ["Choose what this app may do."];
  const bad = (b.ip_addresses || []).filter((ip) => !/^[0-9a-f.:]+(\/\d{1,3})?$/i.test(ip));
  if (bad.length) errors.ip_addresses = [`Not an IP address or range: ${bad.join(", ")}. Use addresses like 102.68.1.4 or ranges like 102.68.0.0/16.`];
  if (Object.keys(errors).length) return { errors };
  return {
    app: {
      ...app, name: b.name, description: b.description ?? null, status: b.status || app.status || "active", ip_addresses: [...new Set(b.ip_addresses)],
      points: b.points, rate_limit_per_minute: b.rate_limit_per_minute ?? null, refresh_url: b.refresh_url ?? null,
    },
  };
};

/* ------------------------------------------------------------------ */
/* Free-trial applications                                              */
/* ------------------------------------------------------------------ */

const TRIAL_STATUSES = { pending: "Waiting for review", approved: "Approved", declined: "Declined" };

const TA = (id, status, legal_name, trading_name, industry, employee_count, city, region, contact, title, email, phone, kra_pin, ago, extra = {}) => ({
  id: `ta-${id}`, reference: `TRL-2026-${String(100 + id).padStart(4, "0")}`, status, legal_name, trading_name, industry, employee_count,
  expected_users: extra.expected_users ?? Math.max(2, Math.round(employee_count / 4)),
  registration_number: `PVT-${(id * 48271) % 900000 + 100000}`, kra_pin, nssf_number: null,
  contact_person_name: contact, contact_person_title: title, contact_email: email, contact_phone: phone, alternate_phone: null,
  website: `https://www.${email.split("@")[1]}`, physical_address: extra.physical_address ?? null, city, region, postal_address: null,
  country: extra.country ?? "Kenya", plan_interest: extra.plan_interest ?? "Business", current_system: extra.current_system ?? "Spreadsheets and email",
  start_when: extra.start_when ?? "Within a month", description: extra.description ?? null,
  documents: extra.documents ?? [{ name: "Certificate of incorporation.pdf", size: 412330 }, { name: "KRA PIN certificate.pdf", size: 128004 }],
  consented_at: daysAgo(ago), visitor_ip: `105.163.${id * 3}.${40 + id}`, created_at: daysAgo(ago), updated_at: daysAgo(ago),
  reviewed_at: status === "pending" ? null : daysAgo(ago - 1), reviewed_by_name: status === "pending" ? null : ADMIN,
  decision_note: extra.decision_note ?? null, tenant_id: extra.tenant_id ?? null,
});

const trials = [
  TA(1, "pending", "Mavuno Agri Inputs Limited", "Mavuno Agri", "Agriculture", 45, "Eldoret", "Uasin Gishu", "Caroline Jepkosgei", "Head of Sales", "caroline@mavunoagri.co.ke", "+254 722 511 340", "P051443210K", 0.3, {
    description: "Distributor of seed and fertiliser to agro-dealers across the North Rift. Want to track dealer leads and field visits.", current_system: "Excel and WhatsApp groups",
  }),
  TA(2, "pending", "Bluewave Insurance Agency Ltd", "Bluewave Insurance", "Insurance", 28, "Nairobi", "Nairobi", "Daniel Mwangi", "Operations Manager", "daniel@bluewave.co.ke", "+254 733 102 998", "P051776651M", 1.2, {
    description: "Insurance agency selling motor and medical cover to SMEs; needs renewals tracking.", plan_interest: "Starter",
  }),
  TA(3, "pending", "Tumaini Microfinance Bank", "Tumaini MFB", "Banking & Finance", 160, "Nakuru", "Nakuru", "Esther Wairimu", "Head of Customer Experience", "esther.wairimu@tumainimfb.co.ke", "+254 711 909 115", "P051998877Q", 2, {
    description: "Microfinance bank with 12 branches; wants a single view of loan customers and complaints.", plan_interest: "Enterprise", current_system: "Legacy core banking CRM module",
  }),
  TA(4, "pending", "Ruwenzori Coffee Exporters", "Ruwenzori Coffee", "Manufacturing", 60, "Kampala", "Central", "Joseph Ssempala", "Commercial Director", "joseph@ruwenzoricoffee.co.ug", "+256 772 640 118", "1000456782", 3, {
    country: "Uganda", description: "Coffee exporter managing buyer relationships in Europe and the Middle East.",
  }),
  TA(5, "approved", "Pwani Logistics Limited", "Pwani Logistics", "Logistics", 22, "Mombasa", "Mombasa", "Hassan Ali", "Director", "hassan@pwanilogistics.co.ke", "+254 722 300 451", "P052112233L", 91, {
    plan_interest: "Starter", tenant_id: 3, decision_note: "Approved: documents in order.",
  }),
  TA(6, "approved", "Harambee Sacco Society Ltd", "Harambee Sacco", "SACCO", 120, "Nairobi", "Nairobi", "Philip Kiptoo", "CEO", "pkiptoo@harambeesacco.co.ke", "+254 733 990 112", "P051998870Q", 262, {
    plan_interest: "Enterprise", tenant_id: 2, decision_note: "Approved: referred by Demulla.",
  }),
  TA(7, "declined", "QuickCash Online Ventures", "QuickCash", "Banking & Finance", 3, "Nairobi", "Nairobi", "Kevin Otieno", "Founder", "kevin@quickcash.example", "+254 700 000 111", "P000000000A", 14, {
    documents: [{ name: "id-front.jpg", size: 86012 }],
    decision_note: "The KRA PIN and registration documents could not be verified. You are welcome to apply again with your certificate of incorporation.",
  }),
  TA(8, "declined", "Kilimo Fresh Exporters Ltd", "Kilimo Fresh", "Agriculture", 35, "Thika", "Kiambu", "Mary Wanjiru", "Sales Lead", "mary@kilimofresh.co.ke", "+254 724 880 412", "P051234567X", 20, {
    decision_note: "This KRA PIN already belongs to a company on DeeCRM360. Please ask your administrator for access instead.",
  }),
];

const trialFlags = (a) => {
  const flags = [];
  const tenant = tenants.find((t) => t.kra_pin === a.kra_pin);
  if (tenant) flags.push({ tone: "danger", text: `KRA PIN ${a.kra_pin} already belongs to ${tenant.name} (${tenant.code}) on DeeCRM360.` });
  const earlier = trials.filter((x) => x.kra_pin === a.kra_pin && x.id !== a.id);
  if (earlier.length) flags.push({ tone: "warning", text: `Earlier applications for this KRA PIN: ${earlier.map((e) => `${e.reference} (${TRIAL_STATUSES[e.status]})`).join(", ")}.` });
  if (tenants.some((t) => t.contact.email.toLowerCase() === a.contact_email.toLowerCase())) flags.push({ tone: "warning", text: `${a.contact_email} is already the contact for a company on DeeCRM360.` });
  return flags;
};

const trialDays = () => plans.find((p) => p.is_trial && p.is_active)?.trial_days ?? 30;

// A harmless stand-in for the short-lived document link.
const sampleDocument = (a, d) =>
  `data:text/plain;charset=utf-8,${encodeURIComponent(`Prototype sample document\n\n${d.name}\nfrom ${a.legal_name} (${a.reference})`)}`;

/* ------------------------------------------------------------------ */
/* Website settings and traffic                                         */
/* ------------------------------------------------------------------ */

const website = {
  company_name: "DeeCRM360", tagline: "Every customer, from first call to renewal.", email: "hello@deecrm360.com", phone: "+254 709 360 360",
  whatsapp: "+254 709 360 361", address: "4th Floor, Westlands Square, Waiyaki Way", city: "Nairobi", country: "Kenya",
  linkedin_url: "https://www.linkedin.com/company/deecrm360", facebook_url: "https://www.facebook.com/deecrm360", x_url: "https://x.com/deecrm360", instagram_url: null,
  sign_in_url: "https://deecrm360.com/sign-in", updated_by_name: ADMIN, updated_at: daysAgo(12),
};

const PAGES = [["/", 10], ["/pricing", 6], ["/features", 4], ["/features/pipeline", 3], ["/features/ticket-api", 2], ["/free-trial", 3], ["/sign-in", 4], ["/about", 1], ["/contact", 1], ["/blog/crm-for-saccos", 2], ["/blog/sales-pipeline-kenya", 1]];
const SOURCES = [["Direct", 8], ["Google", 10], ["LinkedIn", 4], ["Facebook", 2], ["Bing", 1], ["Newsletter", 1], ["demulla.co.ke", 1]];
const DEVICES = [["Desktop", 5], ["Mobile", 6], ["Tablet", 1]];
const BROWSERS = [["Chrome", 9], ["Safari", 3], ["Edge", 2], ["Firefox", 1], ["Samsung Internet", 1]];
const SYSTEMS = [["Android", 6], ["Windows", 5], ["macOS", 2], ["iOS", 3], ["Linux", 1]];
const CITIES_IP = ["105.163", "41.90", "197.248", "102.68", "196.201", "41.80", "154.159", "102.135"];

// Deterministic sample visits over the last 120 days, heavier on weekdays.
const pick = (list, n) => {
  const total = list.reduce((s, [, w]) => s + w, 0);
  let r = n % total;
  for (const [label, w] of list) {
    if (r < w) return label;
    r -= w;
  }
  return list[0][0];
};
const visitsLog = [];
for (let day = 0; day < 120; day++) {
  const date = new Date(Date.now() - day * 864e5);
  const weekend = [0, 6].includes(date.getDay());
  const count = (weekend ? 8 : 18) + ((day * 7) % 11);
  for (let i = 0; i < count; i++) {
    const n = day * 131 + i * 17;
    // About three pages a visit; a few visitors come back on later days.
    const visitor = (day * 9 + Math.floor(i / 3)) % 900;
    const source = pick(SOURCES, visitor * 7 + day);
    const at = new Date(date);
    at.setHours(7 + (n % 14), (n * 7) % 60, (n * 13) % 60, 0);
    if (at > new Date()) at.setTime(Date.now() - (i + 1) * 6e4);
    visitsLog.push({
      id: visitsLog.length + 1, visitor, visited_at: at.toISOString(), ip: `${CITIES_IP[visitor % CITIES_IP.length]}.${(visitor * 7) % 255}.${(visitor * 3) % 250 + 2}`,
      path: pick(PAGES, n + i), source, referrer: { Google: "https://www.google.com/", LinkedIn: "https://www.linkedin.com/", Facebook: "https://m.facebook.com/", Bing: "https://www.bing.com/", "demulla.co.ke": "https://demulla.co.ke/partners" }[source] ?? null,
      device: pick(DEVICES, visitor), browser: pick(BROWSERS, visitor + 3), os: pick(SYSTEMS, visitor + 5),
      campaign: source === "LinkedIn" && visitor % 2 ? "q4-sacco-push" : source === "Newsletter" ? "october-newsletter" : null,
    });
  }
}

// The days asked for, inclusive; the last 30 days when none are given.
const period = (q) => {
  const to = q.to ? new Date(`${q.to}T23:59:59.999`) : new Date(new Date().setHours(23, 59, 59, 999));
  const from = q.from ? new Date(`${q.from}T00:00:00`) : new Date(new Date(to).setDate(to.getDate() - 29));
  from.setHours(0, 0, 0, 0);
  return { from, to };
};
const inPeriod = (q) => {
  const { from, to } = period(q);
  return visitsLog.filter((v) => {
    const t = Date.parse(v.visited_at);
    return t >= from.getTime() && t <= to.getTime();
  });
};
const breakdown = (rows, key, limit = 50) => {
  const groups = {};
  rows.forEach((v) => { (groups[v[key]] ??= new Set()).add(v.visitor); });
  return Object.entries(groups).map(([label, set]) => ({ label, visitors: set.size })).sort((a, b) => b.visitors - a.visitors).slice(0, limit);
};
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const trafficSummary = (q) => {
  const { from, to } = period(q);
  if ((to - from) / 864e5 > 367) return null;
  const rows = inPeriod(q);
  const visitors = new Set(rows.map((v) => v.visitor)).size;
  const views = rows.length;

  const days = [];
  for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
    const key = isoDay(d);
    const dayRows = rows.filter((v) => isoDay(new Date(v.visited_at)) === key);
    days.push({ date: key, label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), visitors: new Set(dayRows.map((v) => v.visitor)).size, views: dayRows.length });
  }

  const pages = {};
  rows.forEach((v) => { const p = (pages[v.path] ??= { label: v.path, views: 0, set: new Set() }); p.views++; p.set.add(v.visitor); });
  const addresses = {};
  rows.forEach((v) => {
    const a = (addresses[v.ip] ??= { ip: v.ip, views: 0, paths: new Set(), first_seen: v.visited_at, last_seen: v.visited_at });
    a.views++; a.paths.add(v.path);
    if (v.visited_at < a.first_seen) a.first_seen = v.visited_at;
    if (v.visited_at > a.last_seen) a.last_seen = v.visited_at;
  });
  const trialsIn = trials.filter((t) => Date.parse(t.created_at) >= from.getTime() && Date.parse(t.created_at) <= to.getTime()).length;

  return {
    from: isoDay(from), to: isoDay(to), timezone: q.tz || "Africa/Nairobi",
    totals: {
      visitors, page_views: views, pages_per_visitor: visitors ? Math.round((views / visitors) * 10) / 10 : 0,
      daily_average: days.length ? Math.round((days.reduce((s, d) => s + d.visitors, 0) / days.length) * 10) / 10 : 0,
      trial_applications: trialsIn, conversion: visitors ? Math.round((trialsIn / visitors) * 1000) / 10 : 0,
    },
    by_day: days,
    pages: Object.values(pages).map((p) => ({ label: p.label, views: p.views, visitors: p.set.size })).sort((a, b) => b.visitors - a.visitors).slice(0, 15),
    sources: breakdown(rows, "source", 12),
    devices: breakdown(rows, "device"),
    browsers: breakdown(rows, "browser", 8),
    systems: breakdown(rows, "os", 8),
    addresses: Object.values(addresses).sort((a, b) => b.views - a.views).slice(0, 20)
      .map((a) => ({ ip: a.ip, views: a.views, pages: a.paths.size, first_seen: a.first_seen, last_seen: a.last_seen })),
  };
};

/* ------------------------------------------------------------------ */
/* Tenant side: apps that call into the company (connected ticket apps) */
/* ------------------------------------------------------------------ */

const tenantPoints = [
  { key: "connection.test", name: "Check the connection", method: "GET", path: "/api/v1/integrations/connection", always: true, description: "Confirms the token works from this address." },
  { key: "tickets.create", name: "Raise a ticket", method: "POST", path: "/api/v1/integrations/tickets", description: "Opens a support ticket for the customer the app belongs to." },
  { key: "tickets.read", name: "Read tickets", method: "GET", path: "/api/v1/integrations/tickets", description: "Tickets the app raised, with their status and SLA." },
  { key: "comments.write", name: "Add a comment", method: "POST", path: "/api/v1/integrations/tickets/{ticket}/comments", description: "A public comment on a ticket the app raised." },
  { key: "attachments.write", name: "Attach a file", method: "POST", path: "/api/v1/integrations/tickets/{ticket}/attachments", description: "Up to 10 MB per file." },
];
const scopeToPoint = { "tickets:create": "tickets.create", "tickets:read": "tickets.read", "comments:write": "comments.write", "attachments:write": "attachments.write" };

const integrationApps = connectedApps.map((a) => ({
  id: `ia-${a.id}`, name: a.name, description: `${a.environment} · client ${a.client_id}`, contact_name: a.contact?.name ?? null, contact_email: a.contact?.email ?? null,
  allowed_ips: [...a.ip_allowlist], points: a.scopes.map((s) => scopeToPoint[s]).filter(Boolean), rate_limit_per_minute: a.rate_limit,
  status: a.status === "suspended" ? "suspended" : "active", suspended_at: a.status === "suspended" ? daysAgo(20) : null,
  suspended_by: a.status === "suspended" ? "Faith Achieng" : null, suspension_reason: a.suspended_reason ?? null,
  last_used_at: a.last_used_at, last_used_ip: a.ip_allowlist[0]?.split("/")[0] ?? null, created_by: "Faith Achieng", created_at: `${a.created_at}T09:00:00.000Z`,
  calls_24h: a.calls_24h, refused_24h: Math.round((a.calls_24h * a.error_rate) / 100),
  tokens: a.status === "revoked" ? [] : [{
    id: `it-${a.id}`, name: `${a.environment} server`, last_four: a.secret_last4, state: a.status === "expired" ? "expired" : "active",
    expires_at: `${a.expires_on}T23:59:59.000Z`, revoked_at: null, revoked_by: null, revoke_reason: null, last_used_at: a.last_used_at,
    last_used_ip: a.ip_allowlist[0]?.split("/")[0] ?? null, created_by: "Faith Achieng", created_at: `${a.secret_created_at}T09:00:00.000Z`,
  }],
}));

const presentIntegration = ({ tokens, calls_24h, refused_24h, ...a }) => ({ ...a, active_tokens: tokens.filter((t) => t.state === "active").length });
const findIntegration = (id) => integrationApps.find((a) => a.id === id);

const integrationLogs = integrationApps.flatMap((a, ai) =>
  a.calls_24h ? Array.from({ length: 12 }, (_, i) => {
    const point = ["tickets.create", "tickets.read", "comments.write", "tickets.read"][i % 4];
    const refused = (i + ai) % 9 === 0;
    return {
      id: `il-${ai}-${i}`, at: hoursAgo(ai * 0.3 + i * 1.9), app: a.name, app_id: a.id, token: a.tokens[0] ? `${a.tokens[0].name} ••••${a.tokens[0].last_four}` : null,
      point, point_name: tenantPoints.find((p) => p.key === point)?.name ?? point, method: point.endsWith("read") ? "GET" : "POST",
      path: tenantPoints.find((p) => p.key === point)?.path.replace("{ticket}", `TKT-04${String(10 + i).padStart(2, "0")}`), ip: a.last_used_ip,
      status: refused ? 429 : point === "tickets.create" ? 201 : 200, outcome: refused ? "refused" : "ok", reason: refused ? "Rate limit reached" : null,
      counts: null, duration_ms: 60 + ((i * 41) % 220),
    };
  }) : []
).sort((x, y) => (x.at < y.at ? 1 : -1));

/* ------------------------------------------------------------------ */

export default [
  // Payment schedules
  ["get", "admin/payment-schedules", () => ok({ schedules: [...schedules].sort((a, b) => a.sort_order - b.sort_order || a.months - b.months) })],
  ["post", "admin/payment-schedules", ({ body }) => {
    if (!body.name) return invalid({ name: ["The name field is required."] });
    if (!body.months) return invalid({ months: ["The months field is required."] });
    if (schedules.some((s) => s.name === body.name)) return invalid({ name: ["There is already a schedule with that name."] });
    if (schedules.some((s) => Number(s.months) === Number(body.months))) return invalid({ months: ["There is already a schedule covering that many months."] });
    const s = {
      id: `sch-${Date.now()}`, name: body.name, months: Number(body.months), discount_percent: Number(body.discount_percent || 0),
      due_days: Number(body.due_days ?? 14), description: body.description ?? null, sort_order: body.sort_order ?? Number(body.months) * 10, is_active: body.is_active !== false,
    };
    schedules.push(s);
    return created(s, `${s.name} added`);
  }],
  ["put", "admin/payment-schedules/:id", ({ params, body }) => {
    const s = schedules.find((x) => x.id === params.id);
    if (!s) return notFound("Payment schedule not found.");
    if (body.is_active === false && !schedules.some((x) => x.is_active && x.id !== s.id)) return fail("At least one payment schedule must stay available.", null, 409);
    Object.assign(s, {
      name: body.name ?? s.name, months: Number(body.months ?? s.months), discount_percent: Number(body.discount_percent ?? s.discount_percent),
      due_days: Number(body.due_days ?? s.due_days), description: body.description ?? null, sort_order: body.sort_order ?? s.months * 10, is_active: body.is_active ?? s.is_active,
    });
    return ok(s, `${s.name} saved`);
  }],
  ["delete", "admin/payment-schedules/:id", ({ params }) => {
    const i = schedules.findIndex((x) => x.id === params.id);
    if (i < 0) return notFound("Payment schedule not found.");
    if (!schedules.some((x) => x.is_active && x.id !== params.id)) return fail("At least one payment schedule must stay available.", null, 409);
    const [s] = schedules.splice(i, 1);
    return ok(null, `${s.name} removed`);
  }],

  // Payment methods
  ["get", "admin/payment-methods", () => ok({
    methods: [...methods].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map(presentMethod),
    confirmations: CONFIRMATIONS,
    types: Object.entries(METHOD_TYPES).map(([key, t]) => ({
      key, label: t.label, automatic: AUTOMATIC.includes(key),
      fields: Object.entries(t.fields).map(([k, [label, required]]) => ({ key: k, label, required })),
    })),
  })],
  ["post", "admin/payment-methods", ({ body }) => {
    if (!body.name) return invalid({ name: ["The name field is required."] });
    if (methods.some((m) => m.name === body.name)) return invalid({ name: ["There is already a payment method with that name."] });
    if (!METHOD_TYPES[body.type]) return invalid({ type: ["Choose what kind of payment this is."] });
    const m = { id: `pm-${Date.now()}`, name: body.name, type: body.type, details: body.details || {}, instructions: body.instructions ?? null, sort_order: body.sort_order ?? 100, is_active: body.is_active !== false, confirmation: body.confirmation || "manual", clearing_days: body.clearing_days ?? null };
    methods.push(m);
    return created(presentMethod(m), `${m.name} added`);
  }],
  ["put", "admin/payment-methods/:id", ({ params, body }) => {
    const m = methods.find((x) => x.id === params.id);
    if (!m) return notFound("Payment method not found.");
    if (body.confirmation === "automatic" && !AUTOMATIC.includes(body.type ?? m.type)) return invalid({ confirmation: ["Only M-Pesa payments can be confirmed automatically. Choose to reconcile this one."] });
    Object.assign(m, {
      name: body.name ?? m.name, type: body.type ?? m.type, details: body.details ?? m.details, instructions: body.instructions ?? null,
      sort_order: body.sort_order ?? 100, is_active: body.is_active ?? m.is_active, confirmation: body.confirmation ?? m.confirmation,
      clearing_days: body.confirmation === "automatic" ? null : body.clearing_days ?? m.clearing_days,
    });
    return ok(presentMethod(m), `${m.name} saved`);
  }],
  ["delete", "admin/payment-methods/:id", ({ params }) => {
    const i = methods.findIndex((x) => x.id === params.id);
    if (i < 0) return notFound("Payment method not found.");
    const [m] = methods.splice(i, 1);
    return ok(null, `${m.name} removed`);
  }],

  // Plans
  ["get", "admin/plans", () => ok({
    plans: [...plans].sort((a, b) => Number(b.is_trial) - Number(a.is_trial) || a.sort_order - b.sort_order || a.price_per_user - b.price_per_user).map(presentPlan),
    catalogue: catalogue.map((m) => ({ ...m, submodules: m.submodules.map((s) => ({ ...s })) })),
    schedules: schedules.filter((s) => s.is_active).sort((a, b) => a.months - b.months).map(({ id, name, months, discount_percent }) => ({ id, name, months, discount_percent })),
  })],
  ["post", "admin/plans/:id/duplicate", ({ params }) => {
    const source = plans.find((p) => p.id === params.id);
    if (!source) return notFound("Plan not found.");
    let name = `${source.name} (copy)`;
    for (let i = 2; plans.some((p) => p.name === name); i++) name = `${source.name} (copy) ${i}`;
    const copy = {
      ...source, id: `plan-${Date.now()}`, name, code: slug(name), is_public: false, is_highlighted: false, is_active: false, is_trial: false, trial_days: null,
      submodule_ids: [...source.submodule_ids], schedules: source.schedules.map((s) => ({ ...s })),
    };
    plans.push(copy);
    return created(presentPlan(copy), `${name} created. It is hidden and switched off until you are ready.`);
  }],
  ["post", "admin/plans", ({ body }) => {
    const { plan, errors } = planFrom(body);
    if (errors) return invalid(errors);
    plan.id = `plan-${Date.now()}`;
    plan.code = slug(plan.name);
    plans.push(plan);
    savePlan(plan);
    return created(presentPlan(plan), `${plan.name} added`);
  }],
  ["put", "admin/plans/:id", ({ params, body }) => {
    const i = plans.findIndex((p) => p.id === params.id);
    if (i < 0) return notFound("Plan not found.");
    const { plan, errors } = planFrom(body, plans[i]);
    if (errors) return invalid(errors);
    plans[i] = plan;
    savePlan(plan);
    return ok(presentPlan(plan), `${plan.name} saved`);
  }],
  ["delete", "admin/plans/:id", ({ params }) => {
    const i = plans.findIndex((p) => p.id === params.id);
    if (i < 0) return notFound("Plan not found.");
    const p = plans[i];
    if (p.is_trial && !plans.some((x) => x.is_trial && x.id !== p.id)) {
      return fail("This is the only free trial plan. New companies start on it, so it cannot be removed. Switch it off instead if you want to stop trials.", null, 409);
    }
    plans.splice(i, 1);
    return ok(null, `${p.name} removed`);
  }],

  // The platform's own integrations
  ["get", "admin/platform-apps", () => ok({
    apps: [...platformApps].sort((a, b) => a.name.localeCompare(b.name)).map(presentPlatformApp),
    points,
    base: origin(),
  })],
  ["post", "admin/platform-apps", ({ body }) => {
    const { app, errors } = appFrom(body);
    if (errors) return invalid(errors);
    Object.assign(app, { id: `pia-${Date.now()}`, status: "active", has_refresh_secret: false, last_refreshed_at: null, last_refresh_result: null, last_used_at: null, last_used_ip: null, tokens: [], calls: [] });
    platformApps.push(app);
    return created(presentPlatformApp(app), `${app.name} added. Issue a token to connect it.`);
  }],
  ["put", "admin/platform-apps/:id", ({ params, body }) => {
    const a = platformApps.find((x) => x.id === params.id);
    if (!a) return notFound("Integration app not found.");
    const { app, errors } = appFrom(body, a);
    if (errors) return invalid(errors);
    Object.assign(a, app);
    return ok(presentPlatformApp(a), `${a.name} saved`);
  }],
  ["delete", "admin/platform-apps/:id", ({ params }) => {
    const i = platformApps.findIndex((x) => x.id === params.id);
    if (i < 0) return notFound("Integration app not found.");
    const [a] = platformApps.splice(i, 1);
    return ok(null, `${a.name} removed and its tokens revoked`);
  }],
  ["post", "admin/platform-apps/:id/tokens", ({ params, body }) => {
    const a = platformApps.find((x) => x.id === params.id);
    if (!a) return notFound("Integration app not found.");
    if (!body.name) return invalid({ name: ['Name the token so you know where it is used, for example "Website server".'] });
    const plain = token("dc360p_");
    const last_four = plain.slice(-4);
    a.tokens.unshift({
      id: `pit-${Date.now()}`, name: body.name, last_four, state: "active", expires_at: body.expires_on ? `${body.expires_on}T23:59:59.000Z` : null,
      revoked_at: null, last_used_at: null, last_used_ip: null, created_at: now(), created_by_name: ADMIN,
    });
    return created({ token: plain, last_four }, "Token issued. Copy it now: it will not be shown again.");
  }],
  ["post", "admin/platform-tokens/:tokenId/revoke", ({ params }) => {
    const t = platformApps.flatMap((a) => a.tokens).find((x) => x.id === params.tokenId);
    if (!t) return notFound("Token not found.");
    if (t.revoked_at) return fail(`"${t.name}" was already revoked on ${new Date(t.revoked_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.`, null, 409);
    Object.assign(t, { revoked_at: now(), state: "revoked" });
    return ok(null, `"${t.name}" revoked. Anything still using it will be refused.`);
  }],
  ["post", "admin/platform-apps/:id/refresh-secret", ({ params }) => {
    const a = platformApps.find((x) => x.id === params.id);
    if (!a) return notFound("Integration app not found.");
    a.has_refresh_secret = true;
    return ok({ secret: token("", 40) }, "New refresh secret made. Copy it into the website's settings now: it will not be shown again.");
  }],
  ["post", "admin/platform-apps/:id/refresh", ({ params }) => {
    const a = platformApps.find((x) => x.id === params.id);
    if (!a) return notFound("Integration app not found.");
    if (!a.refresh_url || !a.has_refresh_secret) return fail("Set the website's refresh address and make a refresh secret first.", null, 409);
    Object.assign(a, { last_refreshed_at: now(), last_refresh_result: "ok: website cache cleared" });
    return ok(null, "The website was refreshed. Visitors see the latest plans and contact details now.");
  }],

  // Free-trial applications
  ["get", "admin/trial-applications", ({ query }) => {
    if (query.status && !TRIAL_STATUSES[query.status]) return invalid({ status: ["The selected status is invalid."] });
    const rows = trials
      .filter((a) => !query.status || a.status === query.status)
      .filter((a) => matches(query.search, a.legal_name, a.trading_name, a.reference, a.kra_pin, a.contact_person_name, a.contact_email))
      .sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1) || (a.created_at < b.created_at ? 1 : -1));
    return ok({
      applications: rows.map((a) => ({
        id: a.id, reference: a.reference, status: a.status, status_label: TRIAL_STATUSES[a.status], legal_name: a.legal_name, trading_name: a.trading_name,
        employee_count: a.employee_count, expected_users: a.expected_users, industry: a.industry, contact_person_name: a.contact_person_name, contact_email: a.contact_email, city: a.city,
        plan_interest: a.plan_interest, documents: a.documents.length, created_at: a.created_at, flagged: trialFlags(a).length > 0,
      })),
      counts: Object.fromEntries(Object.keys(TRIAL_STATUSES).map((k) => [k, trials.filter((a) => a.status === k).length])),
      trial_days: trialDays(),
    });
  }],
  ["get", "admin/trial-applications/:id/documents/:index", ({ params }) => {
    const a = trials.find((x) => x.id === params.id);
    const d = a?.documents[Number(params.index)];
    if (!d) return notFound("Document not found.");
    return ok({ url: sampleDocument(a, d) });
  }],
  ["get", "admin/trial-applications/:id", ({ params }) => {
    const a = trials.find((x) => x.id === params.id);
    if (!a) return notFound("Trial application not found.");
    const tenant = a.tenant_id ? tenants.find((t) => t.id === a.tenant_id) : null;
    return ok({
      ...a, status_label: TRIAL_STATUSES[a.status],
      documents: a.documents.map((d, index) => ({ index, name: d.name, size: d.size ?? null })),
      flags: trialFlags(a),
      tenant: tenant ? { id: tenant.id, code: tenant.code, legal_name: tenant.name, status: tenant.status } : null,
    });
  }],
  ["post", "admin/trial-applications/:id/approve", ({ params, body }) => {
    const a = trials.find((x) => x.id === params.id);
    if (!a) return notFound("Trial application not found.");
    if (!body.address) return invalid({ address: ["Give the address the company will sign in at, like crm.demulla.co.ke."] });
    if (a.status !== "pending") return fail(`${a.reference} has already been ${TRIAL_STATUSES[a.status].toLowerCase()}.`, null, 409);
    const existing = tenants.find((t) => t.kra_pin === a.kra_pin);
    if (existing) return fail(`A company with KRA PIN ${a.kra_pin} already exists: ${existing.name} (${existing.code}). Decline this application or contact them.`, null, 409);
    const code = `CRM${String(tenants.length + trials.filter((t) => t.status === "approved").length + 1).padStart(3, "0")}`;
    Object.assign(a, { status: "approved", reviewed_at: now(), reviewed_by_name: ADMIN, decision_note: null });
    const address = String(body.address).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    return ok({ tenant: { id: `t-${Date.now()}`, code, legal_name: a.legal_name } },
      `${a.trading_name || a.legal_name} is being set up as ${code} on the free trial, signing in at ${address}. ${a.contact_email} will be emailed an invitation once it is ready, usually within a few minutes.`);
  }],
  ["post", "admin/trial-applications/:id/decline", ({ params, body }) => {
    const a = trials.find((x) => x.id === params.id);
    if (!a) return notFound("Trial application not found.");
    if (!body.reason) return invalid({ reason: ["Say why, in words the applicant will read."] });
    if (String(body.reason).length < 10) return invalid({ reason: ["Say a little more: the applicant reads this."] });
    if (a.status !== "pending") return fail(`${a.reference} has already been ${TRIAL_STATUSES[a.status].toLowerCase()}.`, null, 409);
    Object.assign(a, { status: "declined", reviewed_at: now(), reviewed_by_name: ADMIN, decision_note: body.reason });
    return ok(null, `${a.reference} declined. ${a.contact_email} has been told why.`);
  }],

  // Website settings
  ["get", "admin/website-settings", () => ok({ ...website })],
  ["put", "admin/website-settings", ({ body }) => {
    if (!body.company_name) return invalid({ company_name: ["The company name field is required."] });
    const bad = ["linkedin_url", "facebook_url", "x_url", "instagram_url"].filter((k) => body[k] && !/^https?:\/\//.test(body[k]));
    if (bad.length) return invalid(Object.fromEntries(bad.map((k) => [k, ["Use the full address, starting with https://"]])));
    // The form is saved as it stands: anything left out is cleared, not kept.
    Object.keys(website).forEach((k) => { if (!["updated_by_name", "updated_at"].includes(k)) website[k] = body[k] ?? null; });
    Object.assign(website, { updated_by_name: ADMIN, updated_at: now() });
    return ok(null, "Website settings saved. Refresh the website (Administration → Integrations) to show them now.");
  }],

  // Website traffic
  ["get", "admin/website-traffic/visits", ({ query }) => {
    const rows = inPeriod(query)
      .filter((v) => !query.ip || v.ip === query.ip)
      .filter((v) => !query.path || v.path.toLowerCase().includes(String(query.path).toLowerCase()))
      .filter((v) => !query.source || v.source === query.source)
      .sort((a, b) => (a.visited_at < b.visited_at ? 1 : -1));
    const page = paginate(rows, query, 25);
    return ok({
      visits: page.data.map((v) => ({ id: v.id, visited_at: v.visited_at, ip: v.ip, path: v.path, source: v.source, referrer: v.referrer, device: v.device, browser: v.browser, os: v.os, campaign: v.campaign })),
      meta: pageMeta(page),
    });
  }],
  ["get", "admin/website-traffic", ({ query }) => {
    const summary = trafficSummary(query);
    return summary ? ok(summary) : fail("Choose a period of a year or less.", null, 422);
  }],

  // Tenant side: integration apps (no screen calls these yet)
  ["get", "tenant/integrations/logs", ({ query }) => {
    const rows = integrationLogs
      .filter((l) => !query.app_id || l.app_id === query.app_id)
      .filter((l) => !query.outcome || l.outcome === query.outcome)
      .filter((l) => !query.point || l.point === query.point)
      .map(({ app_id, ...l }) => l);
    return ok({ logs: paginate(rows, query, 50) });
  }],
  ["get", "tenant/integrations/apps", ({ query }) => {
    const rows = integrationApps.filter((a) => (!query.status || a.status === query.status) && matches(query.search, a.name, a.contact_name, a.contact_email));
    return ok({
      apps: rows.map((a) => ({ ...presentIntegration(a), calls_24h: a.calls_24h, refused_24h: a.refused_24h })),
      summary: {
        apps: rows.length, active: rows.filter((a) => a.status === "active").length,
        active_tokens: rows.reduce((n, a) => n + a.tokens.filter((t) => t.state === "active").length, 0),
        calls_24h: rows.reduce((n, a) => n + a.calls_24h, 0), refused_24h: rows.reduce((n, a) => n + a.refused_24h, 0),
      },
      points: tenantPoints,
    });
  }],
  ["get", "tenant/integrations/apps/:id", ({ params }) => {
    const a = findIntegration(params.id);
    if (!a) return notFound("Integration app not found.");
    const week = integrationLogs.filter((l) => l.app_id === a.id);
    return ok({
      app: presentIntegration(a), tokens: a.tokens.map((t) => ({ ...t })),
      week: { ok: week.filter((l) => l.outcome === "ok").length * 14, rejected: 0, refused: week.filter((l) => l.outcome === "refused").length, failed: 0 },
      base_url: `${origin()}/api/v1/integrations`, points: tenantPoints,
    });
  }],
  ["post", "tenant/integrations/apps", ({ body }) => {
    if (!body.name) return invalid({ name: ["The name field is required."] });
    const a = {
      id: `ia-${Date.now()}`, name: body.name, description: body.description ?? null, contact_name: body.contact_name ?? null, contact_email: body.contact_email ?? null,
      allowed_ips: body.allowed_ips ?? [], points: body.points ?? [], rate_limit_per_minute: body.rate_limit_per_minute ?? 60, status: "active",
      suspended_at: null, suspended_by: null, suspension_reason: null, last_used_at: null, last_used_ip: null, created_by: "Jane Wanjiku", created_at: now(),
      calls_24h: 0, refused_24h: 0, tokens: [],
    };
    integrationApps.push(a);
    return created({ app: presentIntegration(a) }, `${a.name} created. Issue it a token to start calling.`);
  }],
  ["put", "tenant/integrations/apps/:id", ({ params, body }) => {
    const a = findIntegration(params.id);
    if (!a) return notFound("Integration app not found.");
    ["name", "description", "contact_name", "contact_email", "allowed_ips", "points", "rate_limit_per_minute"].forEach((k) => { if (k in body) a[k] = body[k]; });
    return ok({ app: presentIntegration(a) }, `${a.name} updated.`);
  }],
  ["post", "tenant/integrations/apps/:id/suspend", ({ params, body }) => {
    const a = findIntegration(params.id);
    if (!a) return notFound("Integration app not found.");
    Object.assign(a, { status: "suspended", suspended_at: now(), suspended_by: "Jane Wanjiku", suspension_reason: body.reason ?? null });
    return ok({ app: presentIntegration(a) }, `${a.name} suspended. Its tokens are refused until it is reactivated.`);
  }],
  ["post", "tenant/integrations/apps/:id/activate", ({ params }) => {
    const a = findIntegration(params.id);
    if (!a) return notFound("Integration app not found.");
    Object.assign(a, { status: "active", suspended_at: null, suspended_by: null, suspension_reason: null });
    return ok({ app: presentIntegration(a) }, `${a.name} reactivated.`);
  }],
  ["delete", "tenant/integrations/apps/:id", ({ params }) => {
    const i = integrationApps.findIndex((x) => x.id === params.id);
    if (i < 0) return notFound("Integration app not found.");
    const [a] = integrationApps.splice(i, 1);
    return ok(null, `${a.name} removed and its tokens revoked. Its request log is kept.`);
  }],
  ["post", "tenant/integrations/apps/:id/tokens", ({ params, body }) => {
    const a = findIntegration(params.id);
    if (!a) return notFound("Integration app not found.");
    const plain = token("dc360_");
    const t = {
      id: `it-${Date.now()}`, name: body.name || "Server", last_four: plain.slice(-4), state: "active", expires_at: body.expires_on ? `${body.expires_on}T23:59:59.000Z` : null,
      revoked_at: null, revoked_by: null, revoke_reason: null, last_used_at: null, last_used_ip: null, created_by: "Jane Wanjiku", created_at: now(),
    };
    a.tokens.unshift(t);
    return created({ token: { ...t }, plain_token: plain }, "Token issued. Copy it now — it cannot be shown again.");
  }],
  ["post", "tenant/integrations/apps/:id/tokens/:tokenId/revoke", ({ params, body }) => {
    const t = findIntegration(params.id)?.tokens.find((x) => x.id === params.tokenId);
    if (!t) return notFound("Token not found.");
    Object.assign(t, { state: "revoked", revoked_at: now(), revoked_by: "Jane Wanjiku", revoke_reason: body.reason ?? null });
    return ok({ token: { ...t } }, `"${t.name}" revoked. Calls using it are refused from now on.`);
  }],
];
